// ── NEEDS ATTENTION — the screen ────────────────────────────────────────────
//
// Agent app only, admin only. Never in the customer portal (it carries claim
// estimates, which are an internal admin tool — Kevin, 2026-10-18), and never on Home.
// The rules are in ./needsAttention.js; this file only reads the tables those rules
// name and draws the lines.
//
// App.jsx's styling and data helpers are passed in as `env` rather than re-declared:
// T / uiCard / uiPageTitle / UiSpinner are the app's one set of UI tokens, and
// sbFetch / adminApiFetch / apiJson are its one way to reach Supabase and /api/*.
import React, { useEffect, useMemo, useState } from "react";
import { attention578, signedCopyMissing, ppAttention, jdStalled, feedsMissed, bookCheck,
  dropboxUnreadable, plantedMismatch, unitsBelowGuarantee, claimsLikely, buildLines, filterByOwner, OWNERS, LINE_DEFS } from "./needsAttention";
import * as CAMPAIGN578 from "../campaign578";
import { adjudicateFarm, figuresByCountyCrop } from "../claims/ppAdjuster";
import { buildPriceIndex, buildPpCells, buildModel } from "../claims/ppCells";
import { currentLines } from "../claims/claimExpected";
import { isPreventPlantStage } from "../claims/ppStages";
import { estimateFlagThreshold } from "../claims/claimCrossCheck";
import { resolvePpFactor, ppStateFips } from "../ppFactor";
import { RMA_CROPS } from "../rmaCrops";

const PP_DEPS = { buildPpCells, buildModel, buildPriceIndex, resolvePpFactor, ppStateFips, adjudicateFarm,
  figuresByCountyCrop, currentLines, isPreventPlantStage, estimateFlagThreshold, cropOf: (code) => RMA_CROPS[code] };

const money = (v) => (v == null ? "—" : "$" + Math.round(v).toLocaleString());
const day = (v) => (v ? String(v).slice(0, 10) : "—");

export default function NeedsAttentionView({ isMobile, farmers = [], onNav, isAdmin, env }) {
  const { T, uiCard, uiPageTitle, UiSpinner, sbFetch, adminApiFetch, apiJson } = env;
  const [found, setFound] = useState({});
  const [status, setStatus] = useState({});
  const [owner, setOwner] = useState("All");
  const [open, setOpen] = useState(null);
  const [reload, setReload] = useState(0);
  // The BOOK's crop year (App.jsx CURRENT_CROP_YEAR, data-derived), not the wall clock:
  // on January 1 the calendar flips to a year the book has barely started, and every
  // attention read would come back empty for the year still being worked.
  const cropYear = env.cropYear || new Date().getFullYear();
  const farmName = useMemo(() => new Map((farmers || []).map((f) => [f.id, f.name])), [farmers]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    setFound({}); setStatus({});
    // Each source settles on its own, so a slow or failing one never holds the rest.
    const run = (source, fn) => {
      setStatus((st) => ({ ...st, [source]: "loading" }));
      fn().then((add) => {
        if (!alive) return;
        setFound((f) => ({ ...f, ...add }));
        setStatus((st) => ({ ...st, [source]: "ok" }));
      }).catch((e) => {
        if (alive) setStatus((st) => ({ ...st, [source]: { error: String((e && e.message) || e).slice(0, 160) } }));
      });
    };
    const api = async (url, opts) => apiJson(await adminApiFetch(url, opts), "the server");

    run("578s", async () => {
      const rows = await sbFetch("farmer_578s", { order: "id.asc", select: "id,farmer_id,tax_year,status,forwarded_to_mandy,extracted_name,original_filename,county,received_at" });
      const r = attention578(rows, CAMPAIGN578.acceptedProgramYears());
      return { "578-unmatched": r.unmatched, "578-locked": r.locked, "578-not-sent": r.notSent };
    });
    run("documents", async () => ({ "signed-copy-missing": signedCopyMissing(await sbFetch("farmer_documents", { order: "id.asc",
      select: "id,farmer_id,policy_number,doc_type,crop_year,title,original_filename,signature_status,superseded_by,signed_at",
      filters: ["signature_status=eq.signed_copy_missing"] })) }));
    run("watchdog", async () => ({ "feed-missed": feedsMissed(await sbFetch("pipeline_watchdog_alerts", { order: "id.asc",
      select: "id,feed_id,due_at,state,detail,created_at,recovered_at", filters: ["recovered_at=is.null"] })) }));
    run("bookcheck", async () => {
      const j = await api(`/api/market?fn=audit-sentinel&year=${cropYear}`);
      const b = bookCheck(j.runs || []);
      return { "book-check-failed": b.failed ? [{ run: b.last }] : [],
               "book-check-new": { count: b.failed ? 0 : b.fresh, items: b.last ? [{ run: b.last }] : [] } };
    });
    run("jd", async () => ({ "jd-stalled": jdStalled((await api("/api/market?fn=attention")).jd_stale) }));
    run("planted", async () => ({ "planted-vs-reported": plantedMismatch(
      (await api(`/api/market?fn=attention&part=planted&year=${cropYear}`)).flags) }));
    run("dropbox", async () => ({ "dropbox-unreadable": dropboxUnreadable(
      await api("/api/market?fn=dropbox&action=stage-scan&summary=1", { method: "POST" })) }));
    run("unitguarantee", async () => ({ "unit-below-guarantee": unitsBelowGuarantee(
      (await api(`/api/market?fn=attention&part=unit-guarantee&year=${cropYear}`)).units) }));
    run("claimradar", async () => ({ "claim-likely": claimsLikely((await api("/api/market?fn=claim-radar")).open) }));
    run("pp", async () => {
      const yf = [`crop_year=eq.${cropYear}`];
      const [policies, units, coverages, eligibility, prices, lossLines] = await Promise.all([
        sbFetch("policies", { order: "id.asc", select: "id,farmer_id,crop_year,aip,policy_number,state" }),
        sbFetch("crop_coverage_units", { order: "id.asc", select: "id,policy_id,county,crop,type_code,practice_code,plan,acres,aph_yield,coverage_level,share,is_prevent_plant,liability,acreage_status", filters: yf }),
        sbFetch("crop_coverage", { order: "id.asc", select: "id,policy_id,county,crop,type_code,mpci_plan_code,insurance_options,price_election_pct,iif_key" }),
        sbFetch("pp_eligibility", { order: "id.asc", select: "id,policy_id,county,crop,type,max_acres,planted_acres,remaining_acres" }),
        sbFetch("rma_prices", { order: "id.asc", select: "commodity_code,state_code,type_code,practice_code,projected_price", filters: yf }),
        sbFetch("m13_loss_detail", { order: "id.asc", select: "aip,crop_year,claim_number,iif_key,stage_code,aip_indemnity,synced_at", filters: yf }),
      ]);
      const pol = new Map((policies || []).map((p) => [p.id, p]));
      const r = ppAttention({ policies, units, coverages, eligibility, prices, lossLines, cropYear, deps: PP_DEPS });
      const named = (x) => ({ ...x, farmerId: (pol.get(x.policyId) || {}).farmer_id, policyNumber: (pol.get(x.policyId) || {}).policy_number });
      return { "pp-held": r.held.map(named), "pp-differs": r.differs.map(named) };
    });
    return () => { alive = false; };
  }, [isAdmin, reload, cropYear, sbFetch, adminApiFetch, apiJson]);

  const page = useMemo(() => buildLines(found, status), [found, status]);
  const shown = useMemo(() => filterByOwner(page.lines, owner), [page.lines, owner]);
  const ownsSource = (src) => owner === "All" || Object.values(LINE_DEFS).some((d) => d.source === src && d.owner === owner);
  const unchecked = page.unchecked.filter((u) => ownsSource(u.source));
  const loading = page.loading.filter(ownsSource);

  if (!isAdmin) return <div style={{ ...uiCard(16), color: T.muted }}>This page is for admins.</div>;

  const itemText = (key, it) => {
    const nm = (id) => (id && farmName.get(id)) || null;
    if (key.startsWith("578-")) return [nm(it.farmer_id) || it.extracted_name || it.original_filename || "Unnamed 578",
      [it.county, it.tax_year ? "CY" + it.tax_year : null, "received " + day(it.received_at)].filter(Boolean).join(" · ")];
    if (key === "signed-copy-missing") return [nm(it.farmer_id) || "Unknown farmer",
      [it.title || it.original_filename, it.policy_number, it.crop_year ? "CY" + it.crop_year : null].filter(Boolean).join(" · ")];
    if (key === "pp-held") return [nm(it.farmerId) || it.policyNumber || "Unknown farm",
      `${it.county} ${it.crop} · ${it.ppAcres} ac · ${it.why}`];
    if (key === "pp-differs") return [nm(it.farmerId) || it.policyNumber || "Unknown farm",
      `${it.county ? it.county + " " : ""}${it.crop} · we expect ${money(it.estimate)}, carrier paid ${money(it.paid)}`];
    if (key === "planted-vs-reported") return [nm(it.farmerId) || "Unknown farm", it.text];
    if (key === "unit-below-guarantee") return [nm(it.farmerId) || it.policyNumber || "Unknown farm", it.text];
    if (key === "jd-stalled") return [it.farmer || it.org, it.error ? it.error : it.last_sync_at ? "last synced " + day(it.last_sync_at) : "has never synced"];
    if (key === "feed-missed") return [it.feed_id, `${it.state}${it.due_at ? " · due " + day(it.due_at) : ""}`];
    if (key === "book-check-new") return ["Check of " + day(it.run.started_at),
      `${Number(it.run.new_count) || 0} new, ${Number(it.run.worsened_count) || 0} worse · listed in the Book Integrity email`];
    if (key === "book-check-failed") return [it.run ? "Last check " + day(it.run.started_at) : "No recent check",
      it.run ? `ended ${it.run.status}` : "none in the last 36 hours"];
    if (key === "claim-likely") return [nm(it.farmer_id) || it.title || "Unknown farm",
      [it.title, money(it.amount), it.deadline ? "notice by " + day(it.deadline) : it.deadline_rule ? "deadline: see policy" : null]
        .filter(Boolean).join(" · ")];
    if (key === "dropbox-unreadable") return [String(it.folder || "").split("/").pop() || it.folder, it.stage === "*" ? "the root folder" : "stage " + it.stage];
    return [JSON.stringify(it).slice(0, 80), ""];
  };

  const pill = (active) => ({ padding: "6px 12px", borderRadius: T.r.pill, border: `1px solid ${active ? T.inkSoft : T.lineStrong}`,
    background: active ? T.inkSoft : T.surface, color: active ? "#fff" : T.ink, fontSize: 13, fontWeight: 700, cursor: "pointer" });

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <h2 style={uiPageTitle(isMobile)}>Needs attention</h2>
        <div role="group" aria-label="Owner" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["All", ...OWNERS].map((o) => <button key={o} onClick={() => setOwner(o)} style={pill(owner === o)}>{o}</button>)}
        </div>
      </div>

      <div style={{ ...uiCard(0), overflow: "hidden" }}>
        {shown.map((l) => (
          <div key={l.key} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "12px" : "13px 16px", flexWrap: isMobile ? "wrap" : "nowrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 14.5, color: T.ink, fontWeight: 600 }}>{l.sentence}</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{l.owner}</div>
              </div>
              <button onClick={() => setOpen(open === l.key ? null : l.key)} style={pill(open === l.key)}>
                {open === l.key ? "Close" : "Review"}
              </button>
            </div>
            {open === l.key && (
              <div style={{ background: T.surfaceAlt, padding: isMobile ? "8px 12px 12px" : "8px 16px 14px" }}>
                {l.items.slice(0, 200).map((it, i) => {
                  const [a, b] = itemText(l.key, it);
                  return <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${T.lineSoft}`, fontSize: 13 }}>
                    <span style={{ fontWeight: 700, color: T.ink }}>{a}</span>
                    {b && <span style={{ color: T.muted }}> · {b}</span>}
                  </div>;
                })}
                {l.items.length > 200 && <div style={{ fontSize: 12, color: T.muted, paddingTop: 6 }}>…and {l.items.length - 200} more</div>}
                {onNav && l.nav && <button onClick={() => onNav(l.nav)} style={{ ...pill(false), marginTop: 10 }}>Open where this is fixed →</button>}
              </div>
            )}
          </div>
        ))}

        {loading.length > 0 && (
          <div style={{ padding: "12px 16px", color: T.muted, fontSize: 13 }}><UiSpinner />Checking {loading.length} more…</div>
        )}
        {unchecked.map((u) => (
          <div key={u.source} style={{ padding: "12px 16px", color: T.warn, fontSize: 13, borderTop: `1px solid ${T.lineSoft}` }}>
            Couldn't check {u.label}: {u.why}
          </div>
        ))}
        {!shown.length && !loading.length && !unchecked.length && (
          <div style={{ padding: "18px 16px", color: T.ok, fontSize: 15, fontWeight: 700 }}>
            {owner === "All" ? "Nothing needs anyone today." : `Nothing needs ${owner} today.`}
          </div>
        )}
      </div>
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <button onClick={() => setReload((n) => n + 1)} style={pill(false)}>Check again</button>
      </div>
    </div>
  );
}
